import { type Building, isRunway } from './buildings';
import type { Rng } from './rng';

/*
 * Wind and runway direction. Directions use a 16-point compass (0 = N, 4 = E, 8 = S, 12 = W,
 * 22.5° per step) so wind components come from an integer cosine table instead of Math.cos.
 * On the map +X is east and +Z (tile y) is south.
 */

/** cos(step × 22.5°) × 1000. */
const COS16 = [
  1000, 924, 707, 383, 0, -383, -707, -924, -1000, -924, -707, -383, 0, 383, 707, 924,
] as const;

/** Above this tailwind (knots) the runway in use is turned around. */
export const MAX_TAILWIND = 5;
/** Below this wind speed (knots) the runway direction is left as it is. */
const CALM = 3;
/** Strongest wind the weather produces (knots). */
const MAX_WIND = 38;
/** Typical wind speed (knots): the weather drifts back towards it. */
const MEAN_WIND = 10;
/** A new game starts with a light westerly. */
export const INITIAL_WIND_DIR = 12;
export const INITIAL_WIND_SPEED = 8;

/** Heading flown along a runway: flow 0 lands/departs towards +X (rot 0/2) or +Z (rot 1/3). */
export function runwayHeading(rot: number, flow: number): number {
  const base = rot % 2 === 0 ? 4 : 8;
  return flow === 0 ? base : (base + 8) % 16;
}

/** Runway designator painted on the threshold, e.g. 9 for heading 090°, 27 for 270°. */
export function runwayDesignator(rot: number, flow: number): number {
  const heading = runwayHeading(rot, flow);
  return heading === 0 ? 36 : (heading * 9) / 4;
}

function component(windFrom: number, speed: number, heading: number, offset: number): number {
  const d = (windFrom - heading + offset + 32) % 16;
  return Math.trunc((speed * (COS16[d] ?? 0)) / 1000);
}

/** Wind along the heading, in knots: positive is a headwind, negative a tailwind. */
export function headwind(windFrom: number, speed: number, heading: number): number {
  return component(windFrom, speed, heading, 0);
}

/** Wind across the heading, in knots (always ≥ 0). */
export function crosswind(windFrom: number, speed: number, heading: number): number {
  return Math.abs(component(windFrom, speed, heading, -4));
}

export interface WindState {
  /** Compass step the wind blows from (0–15). */
  windDir: number;
  /** Knots. */
  windSpeed: number;
}

/**
 * Hourly weather: the wind veers or backs a step now and then and its speed wanders around a
 * typical value; every so often a front passes and brings a new wind, rarely a gale.
 */
export function updateWind(w: WindState, rng: Rng): void {
  if (rng.int(100) < 5) {
    w.windDir = rng.int(16);
    w.windSpeed = rng.int(5) === 0 ? rng.range(22, 34) : rng.range(3, 18);
    return;
  }
  if (rng.int(100) < 35) w.windDir = (w.windDir + (rng.int(2) === 0 ? 1 : 15)) % 16;
  const pull = Math.trunc((MEAN_WIND - w.windSpeed) / 4);
  w.windSpeed = Math.max(0, Math.min(MAX_WIND, w.windSpeed + pull + rng.range(-3, 3)));
}

/**
 * Turns runways around when the tailwind on the direction in use gets too strong, as a tower
 * would. Returns true if any runway changed direction.
 */
export function updateRunwayFlows(w: WindState, buildings: readonly Building[]): boolean {
  if (w.windSpeed < CALM) return false;
  let changed = false;
  for (const b of buildings) {
    if (!isRunway(b.type)) continue;
    const tail = -headwind(w.windDir, w.windSpeed, runwayHeading(b.rot, b.flow));
    if (tail > MAX_TAILWIND) {
      b.flow = b.flow === 0 ? 1 : 0;
      changed = true;
    }
  }
  return changed;
}
